import React from 'react';
import * as ENUM from '@dpdgroupuk/redback-enums';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { withProps } from 'recompose';
import { withPageControl } from '@dpdgroupuk/fmx-ui/components/Page';
import { IMAGES } from '@dpdgroupuk/fmx-ui/themes';
import Wizard, { Step } from '@dpdgroupuk/fmx-ui/components/Wizard';
import { withModal } from '@dpdgroupuk/fmx-ui/components/Dialog';
import { makeStyles } from '@material-ui/styles';

import { UPG_STEPS } from '../../steps';
import SelectDayStep from './SelectDayStep';
import SelectTimeStep from './SelectTimeStep';
import CompleteStep from './CompleteStep';
import PaymentStep from './PaymentStep';
import withParcel from '../../../../HOCs/withParcel';
import { fetchActionsDatesByCode } from '../../actions';
import {
  trackable,
  UPGRADE_DELIVERY_OPTION,
} from '../../../../constants/analytics';
import * as MESSAGES from '../../../../constants/message';
import {
  backToDpdSiteHeader,
  backToGreenSiteHeader,
  doSomethingDifferentHeader,
  doSomethingDifferentSpeedDeal,
  getDpdAppHeader,
  goBackToDeliveryDetailsSpeedDeal,
  trackAnotherParcelHeader,
  whatHappensNextHeader,
  whatHappensNextSpeedDeal,
} from '../../../controlActions';
import { withDeliveryUpdate } from '../../hocs';
import { upgradeDelivery } from './actions/upgradeDelivery';
import type { AuthorizedParcel } from '../../../../types';
import GetContactDetailsStep from '../../components/GetContactDetailsStep';
import withFetch from '../../../../HOCs/withFetch';

const useStyles = makeStyles(({ typography, breakpoints }) => ({
  step: {
    backgroundColor: '#fff',
  },
  completeStep: {
    paddingLeft: typography.pxToRem(120),
    paddingRight: typography.pxToRem(120),
    [breakpoints.down('xs')]: {
      paddingLeft: typography.pxToRem(15),
      paddingRight: typography.pxToRem(15),
    },
  },
}));

type Props = {
  parcel: AuthorizedParcel,
  dates: Array<string>,
  updateDelivery: Function,
  onSubmitSuccess: Function,
  onSubmitFailure: Function,
};

const Upgrade = ({
  parcel,
  dates,
  updateDelivery,
  onSubmitSuccess,
  onSubmitFailure,
}: Props) => {
  const styles = useStyles();

  return (
    <Wizard
      actionCode={ENUM.PARCEL_ACTION_CODE.UPG}
      initialStep={UPG_STEPS.SELECT_DAY}
      onSubmit={updateDelivery}
      onSubmitSuccess={onSubmitSuccess}
      onSubmitFailure={onSubmitFailure}
    >
      <Step
        stepId={UPG_STEPS.SELECT_DAY}
        dates={dates}
        component={SelectDayStep}
      />
      <Step
        stepId={UPG_STEPS.CHOOSE_TIME_SLOT}
        component={SelectTimeStep}
        parcel={parcel}
      />
      <Step
        stepId={UPG_STEPS.PAYMENT}
        component={PaymentStep}
        parcel={parcel}
        wrapperClassName={styles.step}
      />
      <Step
        stepId={UPG_STEPS.GET_CONTACTS}
        component={GetContactDetailsStep}
        parcel={parcel}
      />
      <Step
        stepId={UPG_STEPS.COMPLETE}
        component={CompleteStep}
        parcel={parcel}
        actionCode={ENUM.PARCEL_ACTION_CODE.UPG}
        wrapperClassName={styles.completeStep}
      />
    </Wizard>
  );
};

const createSpeedDealControlActions = props => [
  doSomethingDifferentSpeedDeal(props),
  goBackToDeliveryDetailsSpeedDeal(props),
  whatHappensNextSpeedDeal(props),
];

const createHeaderControlActions = props => [
  doSomethingDifferentHeader(props),
  trackAnotherParcelHeader(props),
  whatHappensNextHeader(props),
  backToDpdSiteHeader(props),
  backToGreenSiteHeader(props),
  getDpdAppHeader(props),
];

export default compose(
  withRouter,
  withParcel(),
  withModal,
  withFetch(({ match }) =>
    fetchActionsDatesByCode(match.params.parcelId, ENUM.PARCEL_ACTION_CODE.UPG)
  ),
  withProps(props => ({
    dates: props.fetchedData,
    onSubmitSuccess: ({ goToStep }) => goToStep(UPG_STEPS.COMPLETE),
  })),
  withDeliveryUpdate({
    actionCode: ENUM.PARCEL_ACTION_CODE.UPG,
    changeDeliveryAction: upgradeDelivery,
    trackable: UPGRADE_DELIVERY_OPTION.ON_SUBMIT,
  }),
  withPageControl({
    backgroundImage: IMAGES.MAP,
    trackable: trackable(UPGRADE_DELIVERY_OPTION),
    title: MESSAGES.VIP_MY_PARCEL,
    speedDealActions: createSpeedDealControlActions,
    headerMenuActions: createHeaderControlActions,
  })
)(Upgrade);
